import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  formatDistanceToNowStrict,
} from 'date-fns';
import { ArrowLeft, ExternalLink, X } from 'lucide-react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import { Skeleton } from '@/components/atoms/skeleton';
import { useSession } from '@/features/auth/context/SessionContext';
import { DealPromotionBurst } from '@/features/deals/components/DealPromotionBurst';
import {
  DEALS_MIXPANEL_EVENTS,
  type DealInspectedPayload,
  type DealOpenSource,
} from '@/features/deals/utils/mixpanelEvents';
import {
  MAP_INFO_CARD_BODY_CLASS,
  MAP_INFO_CARD_CLASS,
} from '@/features/map/components/mapBarGeometry';
import { useMixpanel } from '@/hooks/useMixpanel';
import { cn } from '@/libs/utils/cn';
import { formatCompactNumber } from '@/libs/utils/format';
import { formatOwnerName } from '@/libs/utils/formatOwnerName';
import { MAP_DETAIL_CARD_WIDTH } from '../constants';
import { DEFAULT_LAYER_VISIBILITY } from '../hooks/useLayerVisibilityFilters';
import { dealStagePropertyRows } from '../constants/dealStageProperties';
import {
  DEAL_LIVE_STAGE_STYLES,
  DEAL_PHASES,
  type DealStageStyle,
  dealStagePosition,
  dealStageStyle,
} from '../constants/dealStages';
import { useHighestPerformingCompetitorMark } from '../hooks/useHighestPerformingCompetitorMark';
import { useLocationDetailLayers } from '../hooks/useLocationDetailLayers';
import { useLinkedPlace } from '../hooks/useLinkedPlace';
import { useDealByIdQuery } from '../services/deals.api';
import type { StationVisibilityState } from '../utils/applyLayerVisibility';
import { glyphSvg } from '../utils/dealGlyphs';
import { MarkerMetricsBody } from './MarkerMetricsBody';

interface DealMapCardProps {
  map: MapboxMap;
  /** Whether the shell's Mapbox style has loaded — gates the detail overlays. */
  isMapLoaded: boolean;
  /** Whether the shell's icon sprites are ready — gates the detail overlays. */
  imagesInitialized: boolean;
  dealId: string;
  onClose: () => void;
  /** Opens the zone named on the deal. The name is HubSpot's `zone_ref`; the caller resolves the id. */
  onOpenZone?: (zoneName: string) => void;
  /** Competitor station tiles. The crown is skipped while this is off. */
  competitorTilesEnabled?: boolean;
  /** Charge-range, Electra, and low-HPC toggles shared with the station pins. */
  stationFilters?: StationVisibilityState;
  /** Whether this card opened on a deal a creation just landed, which is celebrated on its pin. */
  justPromoted?: boolean;
  /** Called once the celebration has played, so it fires for the promotion and not again. */
  onPromotionCelebrated?: () => void;
  /**
   * What opened the card, reported with the consultation. Defaults to the one the card cannot
   * observe for itself: a deal nothing in this session selected, restored or deep-linked.
   */
  openSource?: DealOpenSource;
  /** Overlay host. Defaults to the Mapbox canvas — pass the chrome layer so the card clears the sidebar. */
  dialogContainer?: HTMLElement;
}

function phaseLabel(style: DealStageStyle): string {
  return DEAL_PHASES.find((phase) => phase.id === style.phase)?.label ?? style.phase;
}

/**
 * "21 days" rather than "about 3 weeks": a rep reads this against a stage SLA, so the strict form is
 * the useful one. Null for a value the API has not sent yet, which is what a frontend deployed
 * ahead of the backend sees.
 */
function formatTimeInStage(enteredStageAt: string | undefined): string | null {
  if (!enteredStageAt) return null;
  const entered = new Date(enteredStageAt);
  return Number.isNaN(entered.getTime()) ? null : formatDistanceToNowStrict(entered);
}

/** The same reading as `formatTimeInStage`, in the form a funnel can be grouped by. */
function daysInStage(enteredStageAt: string | undefined): number | null {
  if (!enteredStageAt) return null;
  const entered = new Date(enteredStageAt);
  return Number.isNaN(entered.getTime()) ? null : differenceInCalendarDays(new Date(), entered);
}

/** One side of the flip. The face turned away leaves the flow ({@link HIDDEN_FACE_CLASS}), so the
 * card box, and the side panel anchored to it, are the size of the face in view. Each face scrolls
 * on its own so the 3D scene is not flattened by a scrollport around it. */
const HIDDEN_FACE_CLASS = 'pointer-events-none absolute inset-x-0 top-0';
/** Border, radius and shadow of a face. The side panel continues them. */
const DEAL_FACE_CHROME =
  'border-[#E5E6E6] shadow-[0_10px_26px_rgba(25,84,94,0.18),0_2px_6px_rgba(25,84,94,0.1)]';
const DEAL_FACE_CLASS = `${MAP_INFO_CARD_BODY_CLASS} ${DEAL_FACE_CHROME} flex h-fit w-full max-h-full flex-col rounded-[14px] group-data-[panel-side=right]/dialog:rounded-r-none border bg-white p-3.5 [backface-visibility:hidden] [grid-area:1/1]`;

/** The deal card, under the search column like the workspace location card. */
export function DealMapCard({
  map,
  isMapLoaded,
  imagesInitialized,
  dealId,
  onClose,
  onOpenZone,
  competitorTilesEnabled = false,
  stationFilters = DEFAULT_LAYER_VISIBILITY,
  justPromoted = false,
  onPromotionCelebrated,
  openSource = 'deep_link',
  dialogContainer,
}: DealMapCardProps) {
  const { data: deal, isError } = useDealByIdQuery(dealId);
  const { user } = useSession();
  const trackAction = useMixpanel((s) => s.trackAction);
  // 'location' flips the card over to the attached location's metrics.
  const [view, setView] = useState<'deal' | 'location'>('deal');
  const zoneRef = deal?.zone_ref || null;

  // A dead deal id (purged from the mirror, or a stale restored `?deal=`) must
  // dismiss the card: the skeleton face has no close button, so an unhandled
  // error would strand it over the map.
  useEffect(() => {
    if (!isError) return;
    toast.error('This deal is no longer available');
    onClose();
  }, [isError, onClose]);

  // The captured-AADT figure and its route overlays are NOT part of
  // `location_results` — they come from the per-location geospatial queries the
  // shared detail-layers hook owns, the same ones the workspace card feeds to
  // `MarkerMetricsBody`. Enabled only on the location face, so opening a deal
  // does not paint radius + AADT overlays nobody asked for.
  const mapRef = useMemo(() => ({ current: map }), [map]);
  const { layersService, capturedAadtData } = useLocationDetailLayers({
    map: mapRef,
    isMapLoaded,
    imagesInitialized,
    currentLocation: view === 'location' ? deal?.location_results?.location : undefined,
  });

  useHighestPerformingCompetitorMark({
    map: mapRef,
    enabled: isMapLoaded && imagesInitialized,
    competitor: view === 'location' ? deal?.location_results?.highest_performing_competitor : null,
    tilesEnabled: competitorTilesEnabled,
    stationFilters,
  });

  // The place the location was created from, or was linked to when the deal was created. On the
  // location face only: the deal face is about the deal.
  useLinkedPlace(
    mapRef,
    deal?.location_results?.location.coordinates,
    view === 'location' && isMapLoaded ? deal?.location_results?.point_of_interest : null,
  );

  const style = deal ? dealStageStyle(deal.stage) : null;
  const position = deal ? dealStagePosition(deal.stage) : 0;
  const stageProperties = deal ? dealStagePropertyRows(deal) : [];
  // Derived rather than mirrored, so it is exact however long ago the deal was last synced.
  const timeInStage = deal ? formatTimeInStage(deal.entered_stage_at) : null;

  // The deals queries poll, so the card re-renders on its own while it is open: without the guard
  // one consultation would be counted as many, and the stage's readership would follow how long
  // the card was left open rather than how often it was opened.
  const inspectionReported = useRef(false);
  useEffect(() => {
    if (inspectionReported.current || !deal || !style) return;
    inspectionReported.current = true;
    const payload: DealInspectedPayload = {
      deal_id: deal.id,
      stage: deal.stage,
      phase: style.phase,
      stage_position: position,
      days_in_stage: daysInStage(deal.entered_stage_at),
      has_location_results: deal.location_results != null,
      has_zone: deal.zone_ref != null && deal.zone_ref !== '',
      has_hubspot_url: deal.hubspot_url != null && deal.hubspot_url !== '',
      // The owner travels as a comparison, never as the address itself.
      is_own_deal:
        deal.owner != null &&
        user?.email != null &&
        deal.owner.toLowerCase() === user.email.toLowerCase(),
      source: openSource,
    };
    trackAction(DEALS_MIXPANEL_EVENTS.DEAL_INSPECTED, payload);
  }, [deal, style, position, openSource, user?.email, trackAction]);

  // Selecting a deal moves no camera — same as a zone. The card opens over
  // whatever the user was already looking at, and panning stays theirs.
  const openLocation = () => {
    if (!deal?.location_results) return;
    trackAction(DEALS_MIXPANEL_EVENTS.LOCATION_METRICS_OPENED, {
      deal_id: deal.id,
      stage: deal.stage,
      has_forecast: deal.location_results.forecast != null,
    });
    setView('location');
  };

  // The burst waits for the deal itself: its point is the pin's, and the pin does not exist until
  // the query lands, so firing earlier would celebrate empty map.
  const promotedPoint =
    justPromoted && deal?.location_results ? deal.location_results.location.coordinates : null;

  return (
    <Dialog
      open
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {promotedPoint && (
        <DealPromotionBurst
          map={map}
          isMapLoaded={isMapLoaded}
          coordinates={promotedPoint}
          onDone={() => onPromotionCelebrated?.()}
        />
      )}
      <DialogContent
        dialogContainer={dialogContainer ?? map.getContainer()}
        showOverlay={false}
        showClose={false}
        position="tl"
        // Chrome-less container: each face carries its own card surface, so the
        // flip rotates the actual card.
        className={cn(
          MAP_INFO_CARD_CLASS,
          'border-0 bg-transparent p-0 shadow-none',
          MAP_DETAIL_CARD_WIDTH,
        )}
        onInteractOutside={(e) => {
          // Non-modal: clicks on the map must NOT dismiss the card.
          e.preventDefault();
        }}
      >
        {deal && style ? (
          // The dialog caps the height; this chain hands that cap down to the faces so a tall
          // face scrolls inside the card instead of growing past it and its side panel.
          <div className="flex min-h-0 flex-col [perspective:1200px]">
            <div
              className={cn(
                'grid min-h-0 grid-rows-[minmax(0,1fr)] transition-transform duration-500 [transform-style:preserve-3d]',
                view === 'location' && '[transform:rotateY(180deg)]',
              )}
            >
              <div className={cn(DEAL_FACE_CLASS, view === 'location' && HIDDEN_FACE_CLASS)}>
                <div className="flex items-start justify-between gap-2">
                  <span
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full py-[3px] pl-1.5 pr-[9px] text-[11px] font-bold leading-[15px]"
                    style={
                      style.hollow
                        ? { color: style.color, boxShadow: `inset 0 0 0 1.5px ${style.color}` }
                        : { backgroundColor: style.color, color: style.fg }
                    }
                  >
                    <span
                      className="flex [&>svg]:size-[11px]"
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG built from static templates, recolored
                      dangerouslySetInnerHTML={{
                        __html: glyphSvg(style.glyphId, 'currentColor'),
                      }}
                    />
                    {deal.stage}
                  </span>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="rounded-md p-0.5 text-[#626968] hover:text-[#021715] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                </div>

                <DialogTitle className="mt-[9px] text-[15px] font-bold leading-5 text-[#021715]">
                  {deal.name}
                </DialogTitle>
                {deal.location_results?.location.address && (
                  <p className="mt-0.5 text-[11px] leading-[15px] text-[#626968]">
                    {deal.location_results.location.address}
                  </p>
                )}

                {/* Owner and time in stage keep their slots even when unknown, so the reader
                    learns the value is missing rather than that it does not apply. */}
                <div className="mt-[9px] flex justify-between gap-4">
                  {/* min-w-0 so the email truncates instead of pushing the time in stage out */}
                  <span className="flex min-w-0 flex-col gap-px text-[10.5px] leading-[14px] text-[#626968]">
                    <b
                      className="truncate text-xs font-bold leading-4 text-[#021715]"
                      title={deal.owner}
                    >
                      {deal.owner ? formatOwnerName(deal.owner) : '—'}
                    </b>
                    Owner
                  </span>
                  <span className="flex shrink-0 flex-col gap-px text-right text-[10.5px] leading-[14px] text-[#626968]">
                    <b className="text-xs font-bold leading-4 text-[#021715]">
                      {timeInStage ?? '—'}
                    </b>
                    In stage
                  </span>
                </div>

                <div className="mt-3 flex gap-0.5">
                  {DEAL_LIVE_STAGE_STYLES.map((liveStyle, index) => (
                    <i
                      key={liveStyle.stage}
                      className="h-1 flex-1 rounded-full"
                      style={{
                        backgroundColor: index < position ? style.arc : '#ECEFEE',
                      }}
                    />
                  ))}
                </div>
                <p className="mt-[5px] text-[10.5px] leading-[14px] text-[#626968]">
                  {position > 0
                    ? `Stage ${position} of ${DEAL_LIVE_STAGE_STYLES.length} · ${phaseLabel(style)}`
                    : `${deal.stage} · out of pipeline`}
                </p>

                <div className="mt-3 flex flex-col gap-1.5 border-t border-[#F2F2F2] pt-[11px] text-xs leading-4">
                  <div className="flex items-baseline justify-between gap-2.5">
                    <span className="text-[#626968]">Est. yearly demand</span>
                    <span className="text-right font-bold text-[#021715]">
                      {deal.location_results?.forecast
                        ? `${formatCompactNumber(deal.location_results.forecast.value)} ${deal.location_results.forecast.unit}`
                        : '—'}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2.5">
                    <span className="text-[#626968]">Site type</span>
                    {deal.landowner_type ? (
                      <span
                        className="truncate text-right font-bold text-[#021715]"
                        title={deal.landowner_type}
                      >
                        {deal.landowner_type}
                      </span>
                    ) : (
                      <span className="italic text-[#626968]">—</span>
                    )}
                  </div>
                  <div className="flex items-baseline justify-between gap-2.5">
                    <span className="text-[#626968]">Hunting zone</span>
                    {zoneRef ? (
                      onOpenZone ? (
                        <button
                          type="button"
                          className="min-w-0 truncate text-right font-bold text-[#021715] underline-offset-2 hover:underline"
                          title={zoneRef}
                          onClick={() => onOpenZone(zoneRef)}
                        >
                          {zoneRef}
                        </button>
                      ) : (
                        <span
                          className="truncate text-right font-bold text-[#021715]"
                          title={deal.zone_ref}
                        >
                          {deal.zone_ref}
                        </span>
                      )
                    ) : (
                      <span className="italic text-[#626968]">No zone</span>
                    )}
                  </div>
                  <div className="flex items-baseline justify-between gap-2.5">
                    <span className="text-[#626968]">First seen</span>
                    <span className="text-right font-bold text-[#021715]">
                      {format(new Date(deal.first_seen_at), 'dd MMM yyyy')}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2.5">
                    <span className="text-[#626968]">Synced</span>
                    <span className="text-right font-bold text-[#021715]">
                      {formatDistanceToNow(new Date(deal.synced_at), { addSuffix: true })}
                    </span>
                  </div>
                </div>

                {stageProperties.length > 0 && (
                  <>
                    <div className="mt-3 mb-2 border-t border-[#F2F2F2] pt-[11px] text-[9.5px] font-bold uppercase leading-none tracking-[0.08em] text-[#626968]">
                      HubSpot · stage properties
                    </div>
                    <dl className="grid grid-cols-2 gap-x-2.5 gap-y-[9px]">
                      {stageProperties.map((row) => (
                        <div
                          key={row.label}
                          className={cn('flex flex-col gap-0.5', row.wide && 'col-span-2')}
                        >
                          <dt className="text-[10.5px] leading-[14px] text-[#626968]">
                            {row.label}
                          </dt>
                          <dd
                            className={cn(
                              'text-xs font-bold leading-4 text-[#021715]',
                              row.value === null && 'font-normal italic text-[#626968]',
                            )}
                          >
                            {row.value ?? '—'}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </>
                )}

                <div className="mt-[13px] flex gap-[7px]">
                  {deal.location_results && (
                    <button
                      type="button"
                      onClick={openLocation}
                      className="inline-flex flex-1 items-center justify-center rounded-full bg-[#0B2A37] px-3 py-[9px] text-xs font-bold leading-4 text-white hover:bg-[#061C25]"
                    >
                      Open location
                    </button>
                  )}
                  {deal.hubspot_url && (
                    <a
                      href={deal.hubspot_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() =>
                        trackAction(DEALS_MIXPANEL_EVENTS.HUBSPOT_OPENED, {
                          deal_id: deal.id,
                          stage: deal.stage,
                          origin: 'card',
                        })
                      }
                      className="inline-flex items-center justify-center gap-[5px] rounded-full border border-[#E5E6E6] px-3 py-[9px] text-xs leading-4 text-[#006369] hover:bg-[#EBFBF9]"
                    >
                      HubSpot
                      <ExternalLink className="size-[11px]" aria-hidden />
                    </a>
                  )}
                </div>
              </div>

              {/* Back face — the attached location, pre-rotated so the flip lands upright. */}
              {deal.location_results && (
                <div
                  className={cn(
                    DEAL_FACE_CLASS,
                    '[transform:rotateY(180deg)]',
                    view === 'deal' && HIDDEN_FACE_CLASS,
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setView('deal')}
                      className="inline-flex items-center gap-1 rounded-md p-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                      Back to deal
                    </button>
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={onClose}
                      className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                  <MarkerMetricsBody
                    locationData={deal.location_results}
                    layersService={layersService}
                    capturedAadtData={capturedAadtData}
                    showExtension={view === 'location'}
                    // The dialog has no border for the panel to overlap, so the panel sits flush.
                    panelClassName={cn(DEAL_FACE_CHROME, 'top-0 bottom-0 rounded-r-[14px]')}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={cn(
              MAP_INFO_CARD_BODY_CLASS,
              'flex w-full flex-col gap-2 rounded-[14px] border border-[#E5E6E6] bg-white p-3.5 shadow-[0_10px_26px_rgba(25,84,94,0.18),0_2px_6px_rgba(25,84,94,0.1)]',
            )}
          >
            <DialogTitle className="sr-only">Deal</DialogTitle>
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
